import api from './axios';
import { signInWithPopup, signInWithCustomToken } from 'firebase/auth';
import { auth, provider } from './firebase';
import { LoginValues, RegisterValues, RegisterResponse, LoginResponse } from './types'
import { getStoredToken, persistSessionValue, persistToken } from '../utils/authStorage';

const authApi = {

    // Login: Email + Password
    login: async (email: string, password: string, phone?: string): Promise<LoginResponse> => {
        try {
            const payload = {
                email,
                password,
                ...(phone?.trim() ? { phone } : {})
            };

            const response = await api.post<LoginResponse>('/auth/login', payload);
            const data = response.data;

            if (data.id_token) {
                sessionStorage.removeItem('user_role');
                localStorage.removeItem('user_role');
                persistToken(data.id_token);
                persistSessionValue('approval_status', String(data.approval_status));
                persistSessionValue('email', data.email);
                persistSessionValue('first_name', data.first_name);
                persistSessionValue('last_name', data.last_name);
                if (data.role) {
                    persistSessionValue('user_role', data.role);
                }
            }

            return data;
        } catch (error: any) {
            const message = error.response?.data?.detail || 'Login failed. Please try again.';
            throw new Error(message);
        }
    },

    // Google Signin
    googleSignIn: async (): Promise<void> => {
        try {
            const result = await signInWithPopup(auth, provider);
            const user = result.user;

            if (!user) throw new Error('No user returned from Firebase.');
            const idToken = await user.getIdToken();

            const response = await api.post('/auth/google-login', { id_token: idToken });
            const user_data = response.data.user;

            sessionStorage.removeItem('user_role');
            localStorage.removeItem('user_role');
            persistToken(idToken);
            persistSessionValue('approval_status', String(user_data.approval_status));
            persistSessionValue('email', user_data.email);
            persistSessionValue('first_name', user_data.first_name);
            persistSessionValue('last_name', user_data.last_name);
            if (user_data.role) {
                persistSessionValue('user_role', user_data.role);
            }
        } catch (error: any) {
            const code = error?.code as string | undefined;
            const msg = error?.message as string | undefined;
            if (
                code === 'ERR_NETWORK' ||
                msg === 'Network Error' ||
                (typeof msg === 'string' && msg.includes('Network Error'))
            ) {
                const base =
                    typeof process.env.REACT_APP_API_URL === 'string' && process.env.REACT_APP_API_URL.trim()
                        ? process.env.REACT_APP_API_URL.trim()
                        : '(not set — requests go to the app origin)';
                throw new Error(
                    `Cannot reach the API (${base}). Set REACT_APP_API_URL=http://localhost:8000 in finz-frontend-v2/.env.dev, restart npm, and ensure the FastAPI container is running on that port (check nothing else is using 8000).`,
                );
            }
            const message = error?.response?.data?.detail || error?.message || 'Authentication failed. Please try again.';
            throw new Error(typeof message === 'string' ? message : 'Authentication failed. Please try again.');
        }
    },

    // Signup Step 1: Details + Password
    signupUser: async (data: RegisterValues): Promise<RegisterResponse> => {
        try {
            const response = await api.post<RegisterResponse>('/auth/register', data);
            const uid = response.data.uid
            const custom_token = response.data.custom_token

            persistSessionValue('custom_token', custom_token)
            persistSessionValue('approval_status', 'false') // Default approval status to false when registering
            persistSessionValue('email', data.email);
            persistSessionValue('phone', '+1' + data.phone);  // USA prefix
            persistSessionValue('first_name', data.first_name);
            persistSessionValue('last_name', data.last_name);

            return response.data;
        } catch (error: any) {
            const message = error || 'Registration failed. Please try again.';
            throw new Error(message);
        }
    },

    // Signup Step 2: Email Verification
    sendEmailOtp: async (email: string): Promise<{ message: string }> => {
        try {
            const response = await api.post('/auth/send-email-otp', { email });
            return response.data;
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to send email OTP.';
            throw new Error(message);
        }
    },
    verifyEmailOtp: async (email: string, otp: string): Promise<{ message: string }> => {
        try {
            const response = await api.post('/auth/verify-email', { email, otp });
            return response.data;
        } catch (error: any) {
            const message = error.response?.data?.message || 'Email verification failed.';
            throw new Error(message);
        }
    },

    // Signup Step 3: Mobile Verification
    sendMobileOtp: async (phone: string): Promise<{ message: string }> => {
        try {
            const response = await api.post('/auth/send-mobile-otp', { phone: phone.trim() });
            return response.data;
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to send mobile OTP.';
            throw new Error(message);
        }
    },
    verifyMobileOtp: async (id_token: string): Promise<{ message: string }> => {
        try {
            const response = await api.post('/auth/verify-mobile', id_token);
            return response.data;
        } catch (error: any) {
            const message = error.response?.data?.message || 'Mobile verification failed.';
            throw new Error(message);
        }
    },

    // Password Reset
    requestPasswordReset: async (email: string): Promise<void> => {
        try {
            await api.post('/auth/request-password-reset', { email });
        } catch (error: any) {
            const message = error.response?.data?.message || 'Password reset request failed.';
            throw new Error(message);
        }
    },
    verifyResetToken: async (
        token: string
    ): Promise<{ status: string; is_valid: boolean; email: string; message: string }> => {
        const response = await api.post("/auth/verify-reset-token", { token });
        return response.data;
    },
    updatePassword: async (
        email: string,
        new_password: string,
        token: string
    ): Promise<{ message: string }> => {
        try {
            const response = await api.post("/auth/update-password", {
                email,
                new_password,
                token,
            });
            return response.data;
        } catch (error: any) {
            const message = error.response?.data?.message
            console.log(message)
            throw new Error(message)
        }

    },


    // Auth User Helpers
    isAuthenticated: (): boolean => !!getStoredToken(),
    getCurrentUser: () => getStoredToken(),

    // Token Helpers
    isTokenExpired: (): boolean => {
        const token = getStoredToken();
        if (!token) return true;
        try {
            const [, payload] = token.split('.');
            const { exp } = JSON.parse(atob(payload));
            return Date.now() >= exp * 1000;
        } catch {
            return true;
        }
    },
    refreshToken: async (refresh_token: string): Promise<{ id_token: string, refresh_token: string, expires_in: 3600 }> => {
        try {
            const response = await api.post('/auth/refresh-token', { refresh_token });
            const newToken = response.data.id_token;
            persistToken(newToken);
            return response.data;
        } catch (error: any) {
            const message = error.response?.data?.message || 'Token refresh failed.';
            console.log(message)
            throw new Error(message);
        }
    },
};

export default authApi;
